import SmsCombinedPanel from "@/components/SmsCombinedPanel";
/**
 * APX WorkCenter — Admin Dashboard (Mr. B)
 * Design: "Gold Standard" — Premium Editorial Finance
 * Layout: Two-column — left: stat cards + Platform Status; right: Inbox panel (sticky tall)
 */

import { useState } from "react";
import { useLocation } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { usePreview, PreviewableRole } from "@/contexts/PreviewContext";
import SendNotificationModal from "@/components/SendNotificationModal";
import FileDropZone from "@/components/FileDropZone";
import { trpc } from "@/lib/trpc";
import AllCommunicationsFeed from "@/components/AllCommunicationsFeed";
import { NewsFeedPanel } from "@/components/NewsFeedPanel";
import { ContactClientModal } from "@/components/ContactClientModal";

import { toast } from "sonner";

// Service key to display config mapping
const SERVICE_DISPLAY: Record<string, { label: string; note: string }> = {
  twilio:           { label: "Twilio",           note: "Primary SMS" },
  zapier:           { label: "Zapier",           note: "Webhook integrations" },
  n8n:              { label: "n8n",              note: "Automation layer" },
  google_workspace: { label: "Google Workspace", note: "3 seats — $66/mo" },
  xero_reports:     { label: "Xero Reports",     note: "Connected via practice login" },
  textellent:       { label: "Textellent",       note: "Legacy — being replaced by Twilio" },
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [showNotify, setShowNotify] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [contactModal, setContactModal] = useState<{ clientName: string; clientEmail?: string | null; clientPhone?: string | null } | null>(null);
  const [showPreviewMenu, setShowPreviewMenu] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const { previewRole, setPreviewRole, isPreviewMode, exitPreview } = usePreview();
  const utils = trpc.useUtils();
  const resetMonthlyMutation = trpc.tierTasks.resetMonthly.useMutation({
    onSuccess: (result) => {
      utils.tierTasks.reconciliation.invalidate();
      utils.clientStatus.listAll.invalidate();
      utils.tierTasks.list.invalidate();
      setShowResetConfirm(false);
      toast.success(`Monthly matrix reset — ${result.resetCount} tasks reset (${result.skippedAnnual} annual clients skipped)`);
    },
    onError: (err) => {
      setShowResetConfirm(false);
      toast.error("Reset failed", { description: err.message });
    },
  });

  const PREVIEW_OPTIONS: { value: PreviewableRole; label: string }[] = [
    { value: "controller", label: "Controller" },
    { value: "bookkeeping_manager", label: "Bkkpr Mgr" },
    { value: "bookkeeper", label: "Bookkeeper" },
    { value: "tax_division", label: "Tax Division" },
  ];
  const { data: coreClientData, isLoading: coreClientLoading } = trpc.xero.getActiveClientCount.useQuery(
    undefined,
    { refetchOnMount: true, refetchOnWindowFocus: true, retry: 3 }
  );
  const { data: zapSummary } = trpc.zapier.summary.useQuery();

  // Portfolio KPI data — derived from tierRecon (server-side, month-aware)
  // tierRecon.total = reportable clients (monthly + bi-annual in active months)
  // tierRecon.nys = not yet started, t1/t2/t3 = in progress tiers, t3c = complete
  const portfolioLoading = false; // tierRecon drives this section
  const { data: xeroActive } = trpc.xero.activeCount.useQuery();
  const activeClientCount = coreClientLoading ? "…" : (coreClientData?.total ?? "—");

  // Live service health checks
  const { data: healthData, isLoading: healthLoading, refetch: refetchHealth } = trpc.serviceHealth.getLatest.useQuery(
    undefined,
    { refetchOnMount: true, refetchOnWindowFocus: false }
  );
  const runHealthChecks = trpc.serviceHealth.runChecks.useMutation({
    onSuccess: () => { refetchHealth(); toast.success("Health checks complete"); },
    onError: () => toast.error("Health check failed"),
  });

  // Build the full platform status list — live health data takes priority over static
  const PLATFORM_STATUS = (() => {
    // Xero org count row (always first)
    const xeroRow = { name: "Xero", status: "Connected", note: coreClientLoading ? "Loading…" : `${coreClientData?.total ?? 0} orgs active`, ok: true, responseMs: null as number | null, lastChecked: null as string | null };
    // Known-connected services with accurate static statuses
    const STATIC_STATUS: Record<string, { status: string; ok: boolean }> = {
      xero_reports:     { status: "Active",           ok: true  },
      n8n:              { status: "Active",           ok: true  },
      twilio:           { status: "Active",           ok: true  },
      textellent:       { status: "Legacy",           ok: false },
      zapier:           { status: "Active",           ok: true  },
      google_workspace: { status: "Active",           ok: true  },
    };
    if (!healthData || healthData.length === 0) {
      // No live ping data yet — show accurate known statuses
      const serviceOrder = ["xero_reports", "n8n", "twilio", "textellent", "zapier", "google_workspace"];
      return [xeroRow, ...serviceOrder.map(key => {
        const d = SERVICE_DISPLAY[key] ?? { label: key, note: "" };
        const s = STATIC_STATUS[key] ?? { status: "Unknown", ok: false };
        return { name: d.label, status: s.status, note: d.note, ok: s.ok, responseMs: null as number | null, lastChecked: null as string | null };
      })];
    }
    // Merge live health data with display config
    const liveMap: Record<string, typeof healthData[0]> = {};
    for (const h of healthData) liveMap[h.serviceKey] = h;
    const serviceOrder = ["xero_reports", "n8n", "twilio", "textellent", "zapier", "google_workspace"];
    const liveRows = serviceOrder.map(key => {
      const live = liveMap[key];
      const display = SERVICE_DISPLAY[key] ?? { label: key, note: "" };
      if (!live) return { name: display.label, status: "Unknown", note: display.note, ok: false, responseMs: null, lastChecked: null };
      const staticS = STATIC_STATUS[key];
      const statusLabel = live.isHealthy
        ? (key === "textellent" ? "Legacy" : "Active")
        : (staticS && staticS.status !== "Unknown" ? staticS.status : "Down");
      const isOk = live.isHealthy || (staticS?.ok ?? false);
      const note = live.statusMessage || display.note;
      return { name: display.label, status: statusLabel, note, ok: isOk, responseMs: live.responseMs, lastChecked: null as string | null };
    });
    return [xeroRow, ...liveRows];
  })();
  const hasZapierData = zapSummary !== undefined;
  const unreviewedSmsValue = zapSummary ? String(zapSummary.unreviewedSms) : "—";

  // Total Xero Active — from coreClients (isActive=true, isPracticeEntity=false, xeroTenantId not null)
  const xeroActiveValue = xeroActive ? String(xeroActive.count) : "—";
  const xeroActiveSub = xeroActive?.lastSyncedAt
    ? (() => {
        const diffMs = Date.now() - new Date(xeroActive.lastSyncedAt).getTime();
        const diffMin = Math.floor(diffMs / 60000);
        if (diffMin < 2) return "Synced just now";
        if (diffMin < 60) return `Synced ${diffMin}m ago`;
        const diffHr = Math.floor(diffMin / 60);
        if (diffHr < 24) return `Synced ${diffHr}h ago`;
        return `Synced ${Math.floor(diffHr / 24)}d ago`;
      })()
    : "Xero source of truth";

  const STAT_CARDS = [
    { label: "Active Clients",      value: String(activeClientCount), sub: "CORE active clients",      color: "oklch(0.08 0.01 240)", onClick: undefined as (() => void) | undefined },
    { label: "Total Xero Active",   value: xeroActiveValue,           sub: xeroActiveSub,              color: "oklch(0.08 0.01 240)", onClick: undefined as (() => void) | undefined },
    { label: "Unreviewed SMS",      value: unreviewedSmsValue,        sub: "Click to open SMS Inbox",  color: "oklch(0.08 0.01 240)", onClick: () => navigate("/sms-inbox") as (() => void) | undefined },
  ];

  // Tier reconciliation data from dedicated procedure
  const { data: tierRecon } = trpc.tierTasks.reconciliation.useQuery(undefined, {
    refetchInterval: 10_000,
    staleTime: 5_000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
  const TIER_RECON_COLS = [
    { key: "nys",  label: "NYS",   value: tierRecon ? String(tierRecon.nys)  : "…", color: "oklch(0.45 0.02 240)", numColor: "oklch(0.12 0.02 240)" },
    { key: "t1",   label: "T1",    value: tierRecon ? String(tierRecon.t1)   : "…", color: "oklch(0.55 0.14 75)",  numColor: "oklch(0.12 0.02 240)" },
    { key: "t2",   label: "T2",    value: tierRecon ? String(tierRecon.t2)   : "…", color: "oklch(0.45 0.15 240)", numColor: "oklch(0.12 0.02 240)" },
    { key: "t3",   label: "T3",    value: tierRecon ? String(tierRecon.t3)   : "…", color: "oklch(0.45 0.18 300)", numColor: "oklch(0.12 0.02 240)" },
    { key: "t3c",  label: "C-P&L", value: tierRecon ? String((tierRecon as any).t3c ?? 0) : "…", color: "oklch(0.40 0.14 145)", numColor: "oklch(0.45 0.16 145)" },
  ] as const;

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 animate-fade-slide-up">

        {/* ── Page header ─────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1
              className="text-2xl font-bold text-[oklch(0.12_0.04_240)]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {getGreeting()}, {user?.name}
            </h1>

          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 shrink-0 items-center">
            <button
              onClick={() => setShowNotify(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
              style={{ background: "oklch(0.92 0.005 240)", border: "1px solid oklch(0.78 0.01 240)", color: "oklch(0.08 0.01 240)" }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              Notify Team
            </button>
            <button
              onClick={() => setShowFiles(!showFiles)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
              style={{ background: "oklch(0.92 0.005 240)", border: "1px solid oklch(0.78 0.01 240)", color: "oklch(0.08 0.01 240)" }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {showFiles ? "Hide Files" : "Send Files"}
            </button>
            {user?.role === "super_admin" || user?.role === "admin" && (
            <button
              onClick={() => setShowResetConfirm(true)}
              disabled={resetMonthlyMutation.isPending}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
              style={{ background: "oklch(0.55 0.18 25 / 12%)", border: "1px solid oklch(0.55 0.18 25 / 50%)", color: "oklch(0.40 0.18 25)", opacity: resetMonthlyMutation.isPending ? 0.6 : 1 }}
              title="SOP: Reset all bookkeeping progress to Not Started for the new month"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v5h5" />
              </svg>
              {resetMonthlyMutation.isPending ? "Resetting…" : "Reset Month"}
            </button>
            )}
            {user?.role === "super_admin" || user?.role === "admin" && (
            <button
              onClick={() => navigate("/settings")}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150"
              style={{ background: "oklch(0.92 0.005 240)", border: "1px solid oklch(0.78 0.01 240)", color: "oklch(0.08 0.01 240)" }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Settings
            </button>
            )}
          </div>
        </div>

        {/* ── File transfer panel ──────────────────────────────────── */}
        {showFiles && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="apx-card rounded-xl p-5">
              <h3 className="text-sm font-semibold text-[oklch(0.12_0.04_240)] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Send Files to Team
              </h3>
              <FileDropZone />
            </div>

          </div>
        )}

        {/* ── Two-column main layout (matches mockup) ──────────────── */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6 items-start">

          {/* LEFT COLUMN: stat cards + Platform Status */}
          <div className="space-y-6">

            {/* Portfolio KPI Bar */}
            <div className="grid grid-cols-2 xl:grid-cols-5 gap-3">
              {[
                { label: "Total Clients",  value: tierRecon ? String((tierRecon as any).total ?? (tierRecon.nys + tierRecon.t1 + tierRecon.t2 + tierRecon.t3 + ((tierRecon as any).t3c ?? 0))) : "…", sub: "T1-assigned portfolio",  color: "oklch(0.08 0.01 240)" },
                { label: "Annual Review",  value: tierRecon ? String((tierRecon as any).biAnnualCount ?? "—") : "…",  sub: "Bi-annual clients",     color: "oklch(0.35 0.08 270)" },
                { label: "Complete",       value: tierRecon ? String((tierRecon as any).t3c ?? 0) : "…",              sub: "C-P&L done",            color: "oklch(0.08 0.01 240)" },
                { label: "In Progress",   value: tierRecon ? String(tierRecon.t1 + tierRecon.t2 + tierRecon.t3) : "…",  sub: "T1/T2/T3 active",      color: "oklch(0.08 0.01 240)" },
                { label: "Not Started",   value: tierRecon ? String(tierRecon.nys) : "…",                              sub: "NYS this month",        color: "oklch(0.22 0.04 240)" },
              ].map(card => (
                <div key={card.label} className="apx-card rounded-xl p-4">
                  <p className="text-sm text-[oklch(0.35_0.03_240)] uppercase tracking-wider mb-1">{card.label}</p>
                  <p className="text-2xl font-bold mb-0.5" style={{ fontFamily: "'Playfair Display', serif", color: card.color }}>{card.value}</p>
                  <p className="text-xs text-[oklch(0.45_0.02_240)]">{card.sub}</p>
                </div>
              ))}
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
              {STAT_CARDS.map((card) => (
                <div
                  key={card.label}
                  className="apx-card rounded-xl p-5 transition-opacity"
                  style={{ cursor: card.onClick ? "pointer" : "default" }}
                  onClick={card.onClick}
                  onMouseEnter={(e) => { if (card.onClick) (e.currentTarget as HTMLElement).style.opacity = "0.8"; }}
                  onMouseLeave={(e) => { if (card.onClick) (e.currentTarget as HTMLElement).style.opacity = "1"; }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-[oklch(0.08_0.01_240)] uppercase tracking-wider font-medium">
                      {card.label}
                    </p>
                    {card.onClick && (
                      <svg className="w-3.5 h-3.5 text-[oklch(0.35_0.03_240)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </div>
                  {card.value === "…" ? (
                    <div className="h-9 w-16 rounded-md mb-1 animate-pulse" style={{ background: "oklch(0.92 0.008 240)" }} />
                  ) : (
                    <p
                      className="text-3xl font-bold mb-1"
                      style={{ fontFamily: "'Playfair Display', serif", color: card.color }}
                    >
                      {card.value}
                    </p>
                  )}
                  <p className="text-sm text-[oklch(0.35_0.03_240)]">{card.sub}</p>
                </div>
              ))}

            </div>

            {/* Platform Status */}
            <div className="apx-card rounded-xl p-5" style={{ border: "2px solid oklch(0.25 0.04 240)" }}>
              <div className="flex items-center justify-between mb-4">
                <h3
                  className="text-base font-semibold text-[oklch(0.08_0.01_240)]"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Platform Status
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[oklch(0.35_0.03_240)]">
                    {PLATFORM_STATUS.filter((p) => p.ok).length}/{PLATFORM_STATUS.length} active
                  </span>
                  <button
                    onClick={() => runHealthChecks.mutate()}
                    disabled={runHealthChecks.isPending || healthLoading}
                    className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all"
                    style={{ background: "oklch(0.90 0.005 240)", border: "1px solid oklch(0.78 0.01 240)", color: "oklch(0.18 0.04 240)", opacity: runHealthChecks.isPending ? 0.6 : 1 }}
                    title="Run live health checks for all services"
                  >
                    <svg className={`w-3 h-3 ${runHealthChecks.isPending ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {runHealthChecks.isPending ? 'Checking…' : 'Refresh'}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {PLATFORM_STATUS.map((p) => (
                  <div
                    key={p.name}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg"
                    style={{ background: "oklch(0.93 0.008 240)" }}
                  >
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{
                        background: p.ok
                          ? "oklch(0.55 0.18 145)"
                          : p.status === "Pending Approval"
                            ? "oklch(0.72 0.18 70)"
                            : "oklch(0.55 0.20 25)",
                        boxShadow: p.ok
                          ? "0 0 6px oklch(0.55 0.18 145 / 60%)"
                          : p.status === "Pending Approval"
                            ? "0 0 6px oklch(0.72 0.18 70 / 60%)"
                            : "0 0 6px oklch(0.55 0.20 25 / 60%)",
                      }}
                    />
                    <span className="text-sm font-medium text-[oklch(0.18_0.04_240)] flex-1">{p.name}</span>
                    <span
                      className="text-sm px-2 py-0.5 rounded-full"
                      style={{
                        background: p.ok
                          ? "oklch(0.55 0.18 145 / 12%)"
                          : p.status === "Pending Approval"
                            ? "oklch(0.72 0.18 70 / 12%)"
                            : "oklch(0.55 0.20 25 / 12%)",
                        color: p.ok
                          ? "oklch(0.30 0.12 145)"
                          : p.status === "Pending Approval"
                            ? "oklch(0.45 0.16 70)"
                            : "oklch(0.45 0.18 25)",
                        border: p.ok
                          ? "1px solid oklch(0.55 0.18 145 / 30%)"
                          : p.status === "Pending Approval"
                            ? "1px solid oklch(0.72 0.18 70 / 30%)"
                            : "1px solid oklch(0.55 0.20 25 / 30%)",
                      }}
                    >
                      {p.status}
                    </span>
                    <span className="text-sm text-[oklch(0.30_0.03_240)] hidden sm:block min-w-[160px] text-right">
                      {p.note}
                      {(p as any).responseMs ? <span className="ml-1 text-xs text-[oklch(0.45_0.02_240)]">({(p as any).responseMs}ms)</span> : null}
                    </span>

                  </div>
                ))}
              </div>
            </div>


          </div>

          {/* RIGHT COLUMN: Quick Actions + Inbox panel — sticky */}
          <div className="xl:sticky xl:top-6 space-y-5">

            {/* Quick Actions */}
            <div className="apx-card rounded-xl p-4">
              <p className="text-sm text-[oklch(0.22_0.04_240)] uppercase tracking-wider font-medium mb-3">Quick Actions</p>
              <div className="grid grid-cols-3 gap-2">

                {/* Notify Team */}
                <button
                  onClick={() => setShowNotify(true)}
                  className="flex flex-col items-center gap-2 py-3 px-2 rounded-xl transition-all duration-150 group"
                  style={{ background: "oklch(0.92 0.005 240 / 8%)", border: "2px solid oklch(0.25 0.04 240)" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "oklch(0.92 0.005 240 / 15%)"; (e.currentTarget as HTMLElement).style.borderColor = "oklch(0.18 0.04 240)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "oklch(0.92 0.005 240 / 8%)"; (e.currentTarget as HTMLElement).style.borderColor = "oklch(0.18 0.04 240)"; }}
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "oklch(0.92 0.005 240 / 15%)" }}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: "oklch(0.08 0.01 240)" }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-center leading-tight" style={{ color: "oklch(0.12 0.04 240)" }}>Notify Team</span>
                </button>

                {/* Contact Client */}
                <button
                  onClick={() => setContactModal({ clientName: "", clientEmail: null, clientPhone: null })}
                  className="flex flex-col items-center gap-2 py-3 px-2 rounded-xl transition-all duration-150"
                  style={{ background: "oklch(0.92 0.005 240 / 8%)", border: "2px solid oklch(0.25 0.04 240)" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "oklch(0.92 0.005 240 / 15%)"; (e.currentTarget as HTMLElement).style.borderColor = "oklch(0.18 0.04 240)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "oklch(0.92 0.005 240 / 8%)"; (e.currentTarget as HTMLElement).style.borderColor = "oklch(0.18 0.04 240)"; }}
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "oklch(0.92 0.005 240 / 15%)" }}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: "oklch(0.08 0.01 240)" }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3v-3z" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-center leading-tight" style={{ color: "oklch(0.12 0.04 240)" }}>Contact Client</span>
                </button>

                {/* Send Files */}
                <button
                  onClick={() => setShowFiles(!showFiles)}
                  className="flex flex-col items-center gap-2 py-3 px-2 rounded-xl transition-all duration-150"
                  style={{ background: "oklch(0.92 0.005 240 / 8%)", border: "2px solid oklch(0.25 0.04 240)" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "oklch(0.92 0.005 240 / 15%)"; (e.currentTarget as HTMLElement).style.borderColor = "oklch(0.18 0.04 240)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "oklch(0.92 0.005 240 / 8%)"; (e.currentTarget as HTMLElement).style.borderColor = "oklch(0.18 0.04 240)"; }}
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "oklch(0.92 0.005 240 / 15%)" }}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: "oklch(0.08 0.01 240)" }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-center leading-tight" style={{ color: "oklch(0.12 0.04 240)" }}>Send Files</span>
                </button>

                {/* Preview As — super_admin only */}
                {user?.role === "super_admin" || user?.role === "admin" && (
                <div className="relative">
                  <button
                    onClick={() => setShowPreviewMenu(!showPreviewMenu)}
                    className="w-full flex flex-col items-center gap-2 py-3 px-2 rounded-xl transition-all duration-150"
                    style={{ background: isPreviewMode ? "oklch(0.72 0.14 75 / 20%)" : "oklch(0.92 0.005 240 / 8%)", border: isPreviewMode ? "2px solid oklch(0.72 0.14 75)" : "2px solid oklch(0.25 0.04 240)" }}
                  >
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: isPreviewMode ? "oklch(0.72 0.14 75 / 30%)" : "oklch(0.92 0.005 240 / 15%)" }}>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: isPreviewMode ? "oklch(0.45 0.12 75)" : "oklch(0.08 0.01 240)" }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-center leading-tight" style={{ color: isPreviewMode ? "oklch(0.45 0.12 75)" : "oklch(0.12 0.04 240)" }}>
                      {isPreviewMode ? "Exit Preview" : "Preview As"}
                    </span>
                  </button>
                  {showPreviewMenu && !isPreviewMode && (
                    <div className="absolute bottom-full left-0 mb-2 w-40 rounded-xl shadow-lg z-50 py-1" style={{ background: "oklch(0.97 0.005 240)", border: "1px solid oklch(0.85 0.01 240)" }}>
                      {PREVIEW_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => { setPreviewRole(opt.value); setShowPreviewMenu(false); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-[oklch(0.92_0.005_240)] transition-colors"
                          style={{ color: "oklch(0.12 0.04 240)" }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {isPreviewMode && (
                    <button
                      onClick={() => { exitPreview(); setShowPreviewMenu(false); }}
                      className="absolute bottom-full left-0 mb-2 w-40 rounded-xl shadow-lg z-50 py-2 px-3 text-sm text-center"
                      style={{ background: "oklch(0.97 0.005 240)", border: "1px solid oklch(0.72 0.14 75)", color: "oklch(0.45 0.12 75)" }}
                    >
                      Exit Preview
                    </button>
                  )}
                </div>
                )}

                {/* View Settings — super_admin only */}
                {user?.role === "super_admin" || user?.role === "admin" && (
                <button
                  onClick={() => navigate("/settings")}
                  className="flex flex-col items-center gap-2 py-3 px-2 rounded-xl transition-all duration-150"
                  style={{ background: "oklch(0.95 0.006 240)", border: "2px solid oklch(0.25 0.04 240)" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "oklch(0.93 0.008 240)"; (e.currentTarget as HTMLElement).style.borderColor = "oklch(0.18 0.04 240)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "oklch(0.95 0.006 240)"; (e.currentTarget as HTMLElement).style.borderColor = "oklch(0.18 0.04 240)"; }}
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "oklch(0.92 0.008 240)" }}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: "oklch(0.14 0.04 240)" }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-center leading-tight" style={{ color: "oklch(0.14 0.04 240)" }}>Settings</span>
                </button>
                )}

              </div>
            </div>

            {/* Tier Pipeline — NYS / T1 / T2 / T3 / T3(C) / Xero */}
            <div className="apx-card rounded-xl p-4">
              <div className="flex items-end justify-between gap-1">
                {TIER_RECON_COLS.map((col) => (
                  <div key={col.key} className="flex flex-col items-center flex-1 gap-0.5">
                    <span className="text-sm font-bold" style={{ color: col.color }}>{col.label}</span>
                    <span
                      className="text-base font-bold tabular-nums"
                      style={{ color: (col as any).numColor ?? "oklch(0.12 0.02 240)" }}
                    >
                      {col.value === "…" ? "…" : col.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

          </div>

        </div>

        {/* ── BOTTOM: Tech/AI News (left) + Business News (right) ── */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mt-5" style={{ minHeight: 420 }}>
          <NewsFeedPanel type="tech" />
          <NewsFeedPanel type="business" />
        </div>

      </div>

      {showNotify && <SendNotificationModal onClose={() => setShowNotify(false)} />}

      {/* Monthly Reset Confirmation Dialog */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "oklch(0 0 0 / 50%)" }}>
          <div className="rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl" style={{ background: "oklch(0.97 0.005 240)", border: "2px solid oklch(0.55 0.18 25 / 40%)" }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "oklch(0.55 0.18 25 / 15%)" }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="oklch(0.45 0.18 25)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-base" style={{ color: "oklch(0.12 0.04 240)", fontFamily: "'Playfair Display', serif" }}>Reset Monthly Matrix</h3>
                <p className="text-xs" style={{ color: "oklch(0.40 0.03 240)" }}>SOP — 5th of the Month</p>
              </div>
            </div>
            <p className="text-sm mb-4" style={{ color: "oklch(0.25 0.03 240)" }}>
              This will reset <strong>all bookkeeping progress</strong> back to <strong>Not Started</strong> for every active client. Due dates, notes, and secondary assignees will be cleared.
            </p>
            <p className="text-xs mb-5 px-3 py-2 rounded-lg" style={{ background: "oklch(0.55 0.18 25 / 8%)", color: "oklch(0.40 0.18 25)", border: "1px solid oklch(0.55 0.18 25 / 25%)" }}>
              This action cannot be undone. Only proceed if you are starting a new billing month.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold"
                style={{ background: "transparent", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.25 0.04 240)" }}
              >
                Cancel
              </button>
              <button
                onClick={() => resetMonthlyMutation.mutate({ callerName: user?.name ?? "Mr. B", callerEmail: user?.email ?? "" })}
                disabled={resetMonthlyMutation.isPending}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-bold"
                style={{ background: "oklch(0.55 0.18 25)", border: "2px solid oklch(0.50 0.18 25)", color: "oklch(0.97 0.005 240)", opacity: resetMonthlyMutation.isPending ? 0.7 : 1 }}
              >
                {resetMonthlyMutation.isPending ? "Resetting…" : "Yes, Reset Now"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Client Modal */}
      {contactModal !== null && (
        <ContactClientModal
          open={true}
          onClose={() => setContactModal(null)}
          clientName={contactModal.clientName}
          clientEmail={contactModal.clientEmail}
          clientPhone={contactModal.clientPhone}
          senderName={user?.name ?? "APX Team"}
          senderEmail={user?.email ?? ""}
          userRole={user?.role ?? "admin"}
        />
      )}
    </DashboardLayout>
  );
}
